import React, { useEffect, useState } from 'react';

const POPUP_KEY = 'thuba_welcome_popup_last_closed';
const INTERVAL_MS = 12 * 60 * 60 * 1000;

const WelcomePopup = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const lastClosed = localStorage.getItem(POPUP_KEY);
        if (!lastClosed || Date.now() - parseInt(lastClosed, 10) > INTERVAL_MS) {
            const t = setTimeout(() => setVisible(true), 700);
            return () => clearTimeout(t);
        }
    }, []);

    const handleClose = () => {
        localStorage.setItem(POPUP_KEY, Date.now().toString());
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={handleClose}
                style={{
                    position: 'fixed', inset: 0, zIndex: 9998,
                    background: 'rgba(5,18,16,0.88)',
                    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                    animation: 'tp-fade-in 0.3s ease forwards',
                }}
            />

            {/* Card */}
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Pesan Selamat Datang"
                style={{
                    position: 'fixed',
                    top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 9999,
                    width: 'min(90vw, 420px)',
                    background: 'linear-gradient(160deg, #0e2e2b 0%, #0c2825 55%, #091f1d 100%)',
                    border: '1px solid rgba(212,175,55,0.3)',
                    borderRadius: '1.2rem',
                    boxShadow: '0 30px 80px rgba(0,0,0,0.75), 0 0 50px rgba(212,175,55,0.07)',
                    fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
                    animation: 'tp-slide-up 0.45s cubic-bezier(0.34,1.4,0.64,1) forwards',
                    overflow: 'hidden',
                }}
            >
                {/* Top gold bar */}
                <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #D4AF37, #F3E5AB, #D4AF37, transparent)' }} />

                {/* Arabic watermark */}
                <div aria-hidden="true" style={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontFamily: "'Amiri','Traditional Arabic',serif",
                    fontSize: '8rem', color: 'rgba(212,175,55,0.04)',
                    whiteSpace: 'nowrap', pointerEvents: 'none', userSelect: 'none', lineHeight: 1,
                }}>بِسْمِ اللَّهِ</div>

                {/* Corner accents */}
                {[{ t: '8px', l: '8px', bt: 'borderTop', bl: 'borderLeft', br: '4px 0 0 0' },
                { t: '8px', r: '8px', bt: 'borderTop', bl: 'borderRight', br: '0 4px 0 0' },
                { b: '8px', l: '8px', bt: 'borderBottom', bl: 'borderLeft', br: '0 0 0 4px' },
                { b: '8px', r: '8px', bt: 'borderBottom', bl: 'borderRight', br: '0 0 4px 0' },
                ].map((c, i) => (
                    <div key={i} aria-hidden="true" style={{
                        position: 'absolute', top: c.t, bottom: c.b, left: c.l, right: c.r,
                        width: '32px', height: '32px',
                        [c.bt]: '1px solid rgba(212,175,55,0.22)',
                        [c.bl]: '1px solid rgba(212,175,55,0.22)',
                        borderRadius: c.br, pointerEvents: 'none',
                    }} />
                ))}

                {/* Close button */}
                <button
                    onClick={handleClose}
                    aria-label="Tutup"
                    style={{
                        position: 'absolute', top: '0.7rem', right: '0.7rem',
                        width: '28px', height: '28px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '50%', cursor: 'pointer', color: '#888', fontSize: '0.75rem',
                        transition: 'all 0.2s ease', zIndex: 2,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,175,55,0.18)'; e.currentTarget.style.color = '#D4AF37'; e.currentTarget.style.borderColor = 'rgba(212,175,55,0.4)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#888'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                >✕</button>

                {/* Content */}
                <div style={{ padding: '1.4rem 1.5rem 1.3rem', position: 'relative', zIndex: 1 }}>

                    {/* Arabic greeting + icon row */}
                    <div style={{ textAlign: 'center', marginBottom: '0.9rem' }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: '46px', height: '46px', borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(212,175,55,0.2) 0%, rgba(212,175,55,0.02) 75%)',
                            border: '1px solid rgba(212,175,55,0.35)', fontSize: '1.3rem',
                            animation: 'tp-float 4s ease-in-out infinite',
                            marginBottom: '0.5rem',
                        }}>🌿</div>
                        <p style={{
                            fontFamily: "'Amiri','Traditional Arabic',serif",
                            fontSize: '1.45rem', color: '#D4AF37',
                            lineHeight: 1.4, margin: 0, letterSpacing: '0.03em',
                        }}>أَهْلاً وَسَهْلاً</p>
                    </div>

                    {/* Divider */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.9rem' }}>
                        <div style={{ flex: 1, height: '1px', background: 'linear-gradient(to right, transparent, rgba(212,175,55,0.3))' }} />
                        <span style={{ color: 'rgba(212,175,55,0.6)', fontSize: '0.6rem' }}>✦</span>
                        <div style={{ flex: 1, height: '1px', background: 'linear-gradient(to left, transparent, rgba(212,175,55,0.3))' }} />
                    </div>

                    {/* Body copy */}
                    <div style={{ color: '#a8b5b3', fontSize: '0.83rem', lineHeight: 1.75, textAlign: 'center', marginBottom: '0.9rem' }}>
                        <p style={{ margin: '0 0 0.6rem', color: '#F2F2F0', fontWeight: 600, fontSize: '0.9rem' }}>
                            Selamat Datang di Thuba Project.
                        </p>
                        <p style={{ margin: '0 0 0.4rem' }}>
                            Alhamdulillah, Allah kehendaki anda hadir disini.
                        </p>
                        <p style={{ margin: 0 }}>
                            Mungkin ini adalah sebuah undangan untuk ikut terlibat dan menebar kebaikan.
                        </p>
                    </div>

                    {/* Ayat card */}
                    <div style={{
                        background: 'rgba(212,175,55,0.06)',
                        border: '1px solid rgba(212,175,55,0.18)',
                        borderLeft: '3px solid rgba(212,175,55,0.55)',
                        borderRadius: '0.65rem',
                        padding: '0.7rem 0.9rem',
                        marginBottom: '0.9rem', textAlign: 'center',
                    }}>
                        <p style={{
                            fontFamily: "'Amiri','Traditional Arabic',serif",
                            fontSize: '1.15rem', color: '#D4AF37', lineHeight: 1.6,
                            margin: '0 0 0.3rem', direction: 'rtl',
                        }}>وَتَعَاوَنُوا عَلَى الْبِرِّ وَالتَّقْوَى</p>
                        <p style={{ color: '#bfc8c6', fontSize: '0.76rem', fontStyle: 'italic', margin: '0 0 0.2rem', lineHeight: 1.5 }}>
                            “Tolong-menolonglah dalam kebaikan dan ketakwaan.”
                        </p>
                        <p style={{ color: 'rgba(212,175,55,0.65)', fontSize: '0.68rem', fontWeight: 600, margin: 0 }}>
                            (QS. Al-Mā'idah: 2)
                        </p>
                    </div>

                    {/* Follow text */}
                    <p style={{ color: '#a8b5b3', fontSize: '0.82rem', lineHeight: 1.7, textAlign: 'center', margin: '0 0 0.85rem' }}>
                        Mari ikuti perjalanan ini dan sebarkan manfaat bersama-sama.<br />
                        <strong style={{ color: '#F2F2F0' }}>Follow media sosial kami</strong> untuk terus terhubung dalam kebaikan.
                    </p>

                    {/* Social buttons — icon only */}
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginBottom: '0.85rem' }}>
                        <a href="https://instagram.com/thubaproject/" target="_blank" rel="noopener noreferrer"
                            aria-label="Instagram"
                            title="Instagram"
                            style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: '44px', height: '44px', borderRadius: '50%',
                                background: 'linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)',
                                color: '#fff', textDecoration: 'none',
                                boxShadow: '0 3px 10px rgba(253,29,29,0.2)',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px) scale(1.08)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(253,29,29,0.4)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 3px 10px rgba(253,29,29,0.2)'; }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                            </svg>
                        </a>
                        <a href="https://bit.ly/3QzpAyL" target="_blank" rel="noopener noreferrer"
                            aria-label="WhatsApp"
                            title="WhatsApp"
                            style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: '44px', height: '44px', borderRadius: '50%',
                                background: 'linear-gradient(135deg,#128c7e,#25d366)',
                                color: '#fff', textDecoration: 'none',
                                boxShadow: '0 3px 10px rgba(37,211,102,0.2)',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px) scale(1.08)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(37,211,102,0.4)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 3px 10px rgba(37,211,102,0.2)'; }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
                            </svg>
                        </a>
                        <a href="https://www.youtube.com/@ThubaProject/streams" target="_blank" rel="noopener noreferrer"
                            aria-label="YouTube"
                            title="YouTube"
                            style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: '44px', height: '44px', borderRadius: '50%',
                                background: 'linear-gradient(135deg,#c4302b,#ff0000)',
                                color: '#fff', textDecoration: 'none',
                                boxShadow: '0 3px 10px rgba(255,0,0,0.2)',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px) scale(1.08)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(255,0,0,0.4)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 3px 10px rgba(255,0,0,0.2)'; }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                            </svg>
                        </a>
                    </div>

                    {/* Closing */}
                    <p style={{ textAlign: 'center', color: '#D4AF37', fontWeight: 600, fontSize: '0.85rem', margin: '0 0 0.85rem' }}>
                        Jazakumullahu Khairan.
                    </p>

                    {/* Tutup */}
                    <button
                        onClick={handleClose}
                        style={{
                            display: 'block', width: '100%', padding: '0.55rem',
                            borderRadius: '9999px',
                            background: 'rgba(212,175,55,0.06)',
                            border: '1px solid rgba(212,175,55,0.18)',
                            color: '#777', fontSize: '0.75rem', cursor: 'pointer',
                            transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,175,55,0.14)'; e.currentTarget.style.color = '#D4AF37'; e.currentTarget.style.borderColor = 'rgba(212,175,55,0.38)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(212,175,55,0.06)'; e.currentTarget.style.color = '#777'; e.currentTarget.style.borderColor = 'rgba(212,175,55,0.18)'; }}
                    >Tutup</button>
                </div>

                {/* Bottom gold bar */}
                <div style={{ height: '2px', background: 'linear-gradient(90deg,transparent,#D4AF37,#F3E5AB,#D4AF37,transparent)', opacity: 0.45 }} />
            </div>

            <style>{`
        @keyframes tp-fade-in { from{opacity:0} to{opacity:1} }
        @keyframes tp-slide-up {
          from { opacity:0; transform:translate(-50%,calc(-50% + 24px)) scale(0.96); }
          to   { opacity:1; transform:translate(-50%,-50%) scale(1); }
        }
        @keyframes tp-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
      `}</style>
        </>
    );
};

export default WelcomePopup;
